import React from 'react';
import { Edit2, Trash2, RotateCcw } from 'lucide-react';
import { ActionItem, EditValues } from '../types';
import { formatDate, getStatusColor, format12Hour } from '../utils';
import TaskForm from './TaskForm';

interface Founder {
  id: string;
  name: string;
}

interface TaskCardProps {
  item: ActionItem;
  isEditing: boolean;
  editValues: EditValues;
  setEditValues: React.Dispatch<React.SetStateAction<EditValues>>;
  hasEdits: boolean;
  draggedItem: string | null;
  allFounders: Founder[];
  onDragStart: (e: React.DragEvent, itemId: string) => void;
  onDragEnd: () => void;
  onCardDragOver: (e: React.DragEvent, targetItemId: string) => void;
  onEditClick: (item: ActionItem) => void;
  onEditSave: (itemId: string) => void;
  onEditCancel: () => void;
  onDelete: (itemId: string) => void;
  onFounderClick?: (founderId: string) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({
  item,
  isEditing,
  editValues,
  setEditValues,
  hasEdits,
  draggedItem,
  allFounders,
  onDragStart,
  onDragEnd,
  onCardDragOver,
  onEditClick,
  onEditSave,
  onEditCancel,
  onDelete,
  onFounderClick,
}) => {
  const renderDescriptionWithFounder = (
    description: string,
    founderName: string,
    founderId: string
  ) => {
    // Strategy 1: Use linked founder if available
    if (founderId && founderName && founderName !== 'No founder') {
      // Try to match full name first
      let parts = description.split(founderName);
      if (parts.length === 2) {
        return (
          <>
            {parts[0]}
            <span className="founder-name-inline" onClick={() => onFounderClick?.(founderId)}>
              {founderName}
            </span>
            {parts[1]}
          </>
        );
      }

      // Try first name (case insensitive)
      const firstName = founderName.split(' ')[0];
      const regex = new RegExp(`\\b(${firstName})\\b`, 'i');
      const match = description.match(regex);

      if (match) {
        const matchedName = match[1];
        const index = description.indexOf(matchedName);
        return (
          <>
            {description.substring(0, index)}
            <span className="founder-name-inline" onClick={() => onFounderClick?.(founderId)}>
              {matchedName}
            </span>
            {description.substring(index + matchedName.length)}
          </>
        );
      }
    }

    // Strategy 2: Fallback - search all founders for name matches
    if (!founderId || !founderName || founderName === 'No founder') {
      for (const founder of allFounders) {
        if (description.includes(founder.name)) {
          const parts = description.split(founder.name);
          if (parts.length === 2) {
            return (
              <>
                {parts[0]}
                <span className="founder-name-inline" onClick={() => onFounderClick?.(founder.id)}>
                  {founder.name}
                </span>
                {parts[1]}
              </>
            );
          }
        }

        const firstName = founder.name.split(' ')[0];
        const regex = new RegExp(`\\b(${firstName})\\b`, 'i');
        const match = description.match(regex);

        if (match) {
          const matchedName = match[1];
          const index = description.indexOf(matchedName);
          return (
            <>
              {description.substring(0, index)}
              <span className="founder-name-inline" onClick={() => onFounderClick?.(founder.id)}>
                {matchedName}
              </span>
              {description.substring(index + matchedName.length)}
            </>
          );
        }
      }
    }

    return description;
  };

  return (
    <div
      className={`trello-card ${draggedItem === item.id ? 'dragging' : ''} ${isEditing ? 'editing' : ''}`}
      draggable={!isEditing}
      onDragStart={(e) => !isEditing && onDragStart(e, item.id)}
      onDragEnd={onDragEnd}
      onDragOver={(e) => !isEditing && onCardDragOver(e, item.id)}
    >
      {isEditing ? (
        <TaskForm
          values={editValues}
          setValues={setEditValues}
          onSave={() => onEditSave(item.id)}
          onCancel={onEditCancel}
          isEditing
          hasChanges={hasEdits}
        />
      ) : (
        <>
          <div className="card-title-row">
            <div className="priority-dot" style={{ backgroundColor: getStatusColor(item.status) }} />
            <h3 className="card-title">
              {renderDescriptionWithFounder(item.title, item.founderName, item.founderId)}
            </h3>
            <div className="card-header-actions">
              {item.recurrence && (
                <div className="recurring-icon" title={`Recurring: ${item.recurrence}`}>
                  <RotateCcw size={12} />
                </div>
              )}
              <button
                className="card-action-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditClick(item);
                }}
                title="Edit task"
              >
                <Edit2 size={12} />
              </button>
              <button
                className="card-action-button card-delete-button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(item.id);
                }}
                title="Delete task"
              >
                <Trash2 size={12} />
              </button>
            </div>
          </div>

          {item.description && item.description !== item.title && (
            <p className="card-description">{item.description}</p>
          )}

          {item.dueDate && (
            <div className="card-footer">
              <div className="card-due-date">
                <span>
                  {formatDate(item.dueDate)}
                  {item.dueTime && ` at ${format12Hour(item.dueTime)}`}
                </span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TaskCard;
